// src/features/landing/infrastructure/surveyApiRepository.js
// 설문 정의 조회·응답 제출 → recruit-api (인증 불필요).
// 계약: risingbrokers-api doc/api.md "설문 (survey)" 절 · 설계 claude-hub
// projects/risingbrokers-api/claude/2610_survey/2610_survey.md
// 오류 본문은 공통 { status, message, timestamp } — message는 화면에 그대로 보여줄 한국어 문장이다.

const API_BASE_URL = process.env.REACT_APP_RECRUIT_API_URL || 'http://localhost:8080';

const NETWORK_ERROR_MESSAGE = '네트워크 연결이 원활하지 않습니다. 잠시 후 다시 시도해 주세요.';
const LOAD_ERROR_MESSAGE = '설문을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.';
const SUBMIT_ERROR_MESSAGE = '설문 제출에 실패했습니다. 잠시 후 다시 시도해 주세요.';

async function request(url, options, defaultMessage) {
    let response;
    try {
        response = await fetch(url, options);
    } catch {
        const error = new Error(NETWORK_ERROR_MESSAGE);
        error.isNetworkError = true;
        throw error;
    }

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || defaultMessage);
        error.status = response.status;
        throw error;
    }

    return response.json().catch(() => ({}));
}

/**
 * GET /api/surveys/{code} — 설문 정의 조회
 * 없는 코드는 404(error.status=404). 마감(status=CLOSED)이어도 200으로 내려온다.
 * @param {string} code 설문 코드 (URL /survey/:code)
 * @returns {Promise<{code: string, status: 'OPEN'|'CLOSED', version: number, cover: Object, questions: Object[]}>}
 */
async function getSurvey(code) {
    return request(
        `${API_BASE_URL}/api/surveys/${encodeURIComponent(code)}`,
        { method: 'GET' },
        LOAD_ERROR_MESSAGE,
    );
}

/**
 * POST /api/surveys/{code}/responses — 설문 응답 제출 (201 { id, createdAt })
 * 400: 검증 실패·마감(message 그대로 표시) / 409: version 불일치(새로고침 필요) / 404: 없는 코드
 * @param {string} code 설문 코드
 * @param {Object} data
 * @param {number} data.version GET으로 받은 정의의 version
 * @param {Object} data.answers { 답변 키: 값 } — choice는 질문 id: 선택지 value(문자열), number는 필드 key: 숫자
 * @param {string} data.company 회사명 (≤100)
 * @param {string} data.name 이름 (≤50)
 * @param {string} data.phone 연락처 숫자만
 * @param {boolean} data.privacyAgreed true만 허용
 * @param {string} [data.utmSource]
 * @param {string} [data.utmMedium]
 * @param {string} [data.utmCampaign]
 * @param {string} [accessToken] 로그인 상태면 전달 — 서버가 member_id에 기록. 없어도 제출 가능(비회원 응답).
 * @returns {Promise<{id: number, createdAt: string}>}
 */
async function submitResponse(code, data, accessToken) {
    const headers = { 'Content-Type': 'application/json' };
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
    return request(
        `${API_BASE_URL}/api/surveys/${encodeURIComponent(code)}/responses`,
        { method: 'POST', headers, body: JSON.stringify(data) },
        SUBMIT_ERROR_MESSAGE,
    );
}

export const surveyRepository = {
    getSurvey,
    submitResponse,
};
